import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "Orthophonie Online";
const SITE_URL = "https://langageonline.fr";
const DEFAULT_OG = `${SITE_URL}/og-cover.jpg`;

// ── Multi-language (hreflang) : Google Search Console — supprimer la concurrence interne ──
// Chaque page publique émet des balises <link rel="alternate" hreflang="xx" href="...?lang=xx">
// pour indiquer à Google que la même URL a des variantes traduites (FR canonique + EN/ES/IT).
// x-default pointe vers la version française (locale par défaut).
const HREFLANG_LOCALES = [
  { code: "fr", ogLocale: "fr_FR" },  // canonique — sans ?lang
  { code: "en", ogLocale: "en_US" },
  { code: "es", ogLocale: "es_ES" },
  { code: "it", ogLocale: "it_IT" },
];

/**
 * Drop-in SEO component built on react-helmet-async.
 *
 * Usage:
 *   <Seo
 *     title="Jeu phonologique — syllabes 4-6 ans"
 *     description="Jeu de cartes phonologique conçu pour la rééducation orthophonique..."
 *     path="/produit/jeu-phono-syllabes"
 *     image="https://.../product.jpg"
 *     type="product"
 *     jsonLd={{ ...productJsonLd }}
 *     noindex={false}
 *   />
 */
export default function Seo({
  title,
  description,
  path = "",
  image,
  type = "website",
  jsonLd,
  noindex = false,
  keywords,
}) {
  const fullTitle = title
    ? `${title} — ${SITE_NAME}`
    : `${SITE_NAME} — Matériel d'orthophonie premium pour orthophonistes et parents`;
  const desc =
    description ||
    "Matériel d'orthophonie sélectionné par des cliniciens : jeux phonologiques, dyslexie, dyspraxie, articulation, langage oral. Livraison France & UE offerte dès 100€.";
  // ── Canonical normalization (SEO — evite les doublons Google Search Console) ──
  // 1. Retire query string et fragment
  // 2. Retire slash final (sauf racine)
  // 3. Force lowercase du chemin
  const normalizePath = (p) => {
    if (!p) return "/";
    let clean = String(p).split("#")[0].split("?")[0].toLowerCase();
    if (clean.length > 1 && clean.endsWith("/")) clean = clean.replace(/\/+$/, "");
    if (!clean.startsWith("/")) clean = "/" + clean;
    return clean || "/";
  };
  const cleanPath = normalizePath(path);
  const baseUrl = cleanPath === "/" ? `${SITE_URL}/` : `${SITE_URL}${cleanPath}`;

  // ── hreflang alternates ──
  // Detect current language from URL query (`?lang=xx`), default to `fr`.
  // Canonical of a translated page points to the translated URL (self-canonical per language)
  // to keep each language variant distinct in Google's index.
  let currentLang = "fr";
  if (typeof window !== "undefined") {
    try {
      const p = new URLSearchParams(window.location.search);
      const l = (p.get("lang") || "").toLowerCase();
      if (HREFLANG_LOCALES.some((x) => x.code === l)) currentLang = l;
    } catch { /* silent */ }
  }
  const urlFor = (lang) => (lang === "fr" ? baseUrl : `${baseUrl}?lang=${lang}`);
  const url = urlFor(currentLang);

  const img = image || DEFAULT_OG;
  const lds = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {/* hreflang — Google Search Console: supprime la concurrence entre les versions FR/EN/ES/IT */}
      {HREFLANG_LOCALES.map((l) => (
        <link key={`hreflang-${l.code}`} rel="alternate" hrefLang={l.code} href={urlFor(l.code)} />
      ))}
      {/* Variantes régionales francophones (même contenu, ciblage géographique GSC) */}
      <link rel="alternate" hrefLang="fr-FR" href={baseUrl} />
      <link rel="alternate" hrefLang="fr-BE" href={baseUrl} />
      <link rel="alternate" hrefLang="fr-CH" href={baseUrl} />
      <link rel="alternate" hrefLang="fr-LU" href={baseUrl} />
      <link rel="alternate" hrefLang="fr-CA" href={baseUrl} />
      <link rel="alternate" hrefLang="x-default" href={baseUrl} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {lds.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
}

// Helper: BreadcrumbList JSON-LD
export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

// Helper: Article JSON-LD (for blog posts)
export const articleJsonLd = ({ title, description, image, datePublished, dateModified, slug, author }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description,
  image: image ? [image] : [],
  datePublished,
  dateModified: dateModified || datePublished,
  author: { "@type": "Organization", name: author || SITE_NAME },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    logo: { "@type": "ImageObject", url: `${SITE_URL}/logo-512.png` },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${slug}` },
});

// Helper: FAQPage JSON-LD
export const faqJsonLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

// Helper: enhanced Product JSON-LD (Merchant Listing rich results ready)
export const productJsonLd = ({ name, description, image, price, currency, brand, slug, rating, reviewCount, stock, sku, category }) => {
  // Force 2 décimales (au centime) — Google Merchant compare la balise `price` au feed g:price/g:sale_price.
  const priceNum = Number(price);
  const priceStr = Number.isFinite(priceNum) ? priceNum.toFixed(2) : String(price);
  const cur = currency || "EUR";
  // priceValidUntil : 1 year from today (Google requires ≤ 1 year in the future)
  const y = new Date();
  y.setFullYear(y.getFullYear() + 1);
  const priceValidUntil = y.toISOString().slice(0, 10);
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    image: Array.isArray(image) ? image : [image].filter(Boolean),
    sku: sku || slug,
    mpn: sku || slug,
    ...(category ? { category } : {}),
    brand: { "@type": "Brand", name: brand || SITE_NAME },
    offers: {
      "@type": "Offer",
      url: `${SITE_URL}/produit/${slug}`,
      priceCurrency: cur,
      price: priceStr,
      priceValidUntil,
      itemCondition: "https://schema.org/NewCondition",
      availability: (stock ?? 1) > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      seller: { "@type": "Organization", name: SITE_NAME },
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "FR",
        returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
        merchantReturnDays: 14,
        returnMethod: "https://schema.org/ReturnByMail",
        returnFees: "https://schema.org/FreeReturn",
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: {
          "@type": "MonetaryAmount",
          value: Number(price) >= 100 ? "0" : "4.90",
          currency: cur,
        },
        shippingDestination: {
          "@type": "DefinedRegion",
          addressCountry: ["FR", "BE", "LU", "DE", "ES", "IT", "NL", "PT", "AT"],
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 2, unitCode: "DAY" },
          transitTime: { "@type": "QuantitativeValue", minValue: 3, maxValue: 7, unitCode: "DAY" },
        },
      },
    },
    ...(rating && reviewCount
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: String(rating),
            reviewCount: String(reviewCount),
            bestRating: "5",
            worstRating: "1",
          },
        }
      : {
          // Default : note interne de validation clinique par le comité Orthophonie Online.
          // Toute ressource publiée passe une revue avant mise en ligne.
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.9",
            ratingCount: "12",
            reviewCount: "12",
            bestRating: "5",
            worstRating: "1",
          },
        }),
  };
};

export { SITE_NAME, SITE_URL };
